import { useCallback, useEffect, useState } from 'react';

// Backstop for dismissing the completion overlay when the fade never reports finishing. Comfortably
// longer than OverlayProgressBar's 600ms hold plus its 400ms fade, so the normal path always wins
// and this only runs when the transitionend event is never delivered.
export const OVERLAY_DISMISS_FALLBACK_MS = 2000;

/**
 * Manages the show/fade overlay lifecycle for batched report loading.
 *
 * The overlay goes through three states:
 *   1. Hidden  — `showOverlay=false`, nothing rendered
 *   2. Loading — `isLoading=true` flips `showOverlay=true`, spinner visible
 *   3. Complete — `isLoading` goes false while `showOverlay` is still true,
 *      so `isComplete` becomes true. The consumer's OverlayProgressBar shows
 *      a "Complete!" message, holds briefly, then fades out. When the CSS
 *      fade transition ends, the consumer calls `handleComplete` to flip
 *      `showOverlay` back to false, unmounting the overlay.
 *
 * This intentionally does NOT auto-hide on the success path — the consumer
 * controls dismissal via `handleComplete` so the fade animation can finish
 * naturally.
 *
 * `interrupted` is the exception: a run that was cancelled or that failed also
 * clears `isLoading`, which would otherwise put the overlay into state 3 and
 * announce "Complete!" for a report that did no such thing. When it is set the
 * overlay is gone in the same render — not dismissed afterwards by an effect,
 * which would leave one committed frame showing the spinner at 0% with a live
 * Cancel button in it. Defaults to false, so callers that don't pass it (CLE
 * Report) behave exactly as before. See PA-3289.
 */
export const useOverlayTransition = (isLoading: boolean, interrupted = false) => {
  // Whether a run has put the overlay up. Visibility is derived from it below rather than read
  // directly, so an interrupted run needs no state change to take the overlay off screen.
  const [shown, setShown] = useState(false);

  const showOverlay = shown && !interrupted;

  // True when loading just finished but the overlay hasn't been dismissed yet.
  // This is the window where OverlayProgressBar shows "Complete!" + fade.
  const isComplete = !isLoading && showOverlay;

  // Show the overlay as soon as loading starts. On the success path we never
  // hide it here — hiding is deferred to handleComplete so the fade plays out.
  useEffect(() => {
    if (isLoading) setShown(true);
  }, [isLoading]);

  // Lower the latch too. The derivation above already hides the overlay for as long as the run is
  // flagged, but leaving the latch raised would bring it back the moment the flag clears without a
  // new run — an overlay with nothing behind it.
  useEffect(() => {
    if (interrupted) setShown(false);
  }, [interrupted]);

  // Dismissal normally comes from handleComplete below, which the consumer calls when the CSS fade
  // ends. A transitionend only fires if the transition actually runs, and it does not when the
  // element stops being rendered part-way through — switching report tabs mid-fade is enough, and
  // since both tab panes are mounted, neither copy fires it. The overlay then stays up for good
  // with the finished report behind it, until the user leaves the page entirely.
  //
  // So the completion state gets a deadline as well as an event.
  useEffect(() => {
    if (!isComplete) return undefined;

    const timer = setTimeout(() => setShown(false), OVERLAY_DISMISS_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [isComplete]);

  // Called by the consumer after the fade-out transition ends.
  const handleComplete = useCallback(() => setShown(false), []);

  return { showOverlay, isComplete, handleComplete };
};
